import { QueryClient } from '@tanstack/react-query'

import { UserFilters } from './types'

// Default query options
export const defaultQueryOptions = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  retry: (failureCount: number, error: any) => {
    // Only retry on server errors (500-599 status codes)
    const status = error?.response?.status

    if (status && status >= 500 && status < 600) {
      return failureCount < 3
    }

    return false
  },
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
}

// Specific query options for different data types
export const queryOptions = {
  // Auth queries - user data changes frequently and needs to be fresh
  auth: {
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter stale time for user data
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to the app
    retry: (failureCount: number, error: any) => {
      // Only retry on server errors (500-599 status codes)
      const status = error?.response?.status

      if (status && status >= 500 && status < 600) {
        return failureCount < 3
      }

      return false
    },
  },

  // Tenant queries - admin management requires fresh data, especially when filtering by status
  tenants: {
    staleTime: 0, // Always fetch fresh data to ensure filters show accurate results
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache but always refetch
    refetchOnWindowFocus: true, // Refetch when user returns to ensure fresh data
  },

  // Health queries - check frequently
  health: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 60 * 1000, // Refetch every minute
  },

  // File queries - moderate changes
  files: {
    staleTime: 0, // No caching
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  // Audit trail queries - rarely changes
  auditTrail: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },

  // Security queries - generated on demand, no caching needed
  security: {
    staleTime: 0, // No caching for security data
    gcTime: 0, // No caching for security data
  },

  // Reseller queries - moderate changes
  resellers: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
  },

  // Plan queries - moderate changes, but should be fresh for billing
  plans: {
    staleTime: 0, // No caching
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to the app
  },

  // API Key queries - security sensitive, should be fresh
  apiKeys: {
    staleTime: 0, // No caching
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to the app
  },

  reports: {
    taleTime: 0, // No caching
    gcTime: 0, // No caching
  },

  billing: {
    staleTime: 0, // No caching
    gcTime: 0, // No caching
  },

  quota: {
    staleTime: 0, // No caching
    gcTime: 0, // No caching
  },
} as const

// Create and configure the query client with enhanced error handling
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...defaultQueryOptions,
    },
    mutations: {
      retry: (failureCount: number, error: any) => {
        // Only retry on server errors (500-599 status codes)
        const status = error?.response?.status

        if (status && status >= 500 && status < 600) {
          return failureCount < 1
        }

        return false
      },
      retryDelay: 1000,
      // Global error handler for mutations
      onError: (error: any) => {
        console.error('Mutation error:', error)
      },
    },
  },
})

// Query keys factory for better type safety and consistency
export const queryKeys = {
  // Auth queries
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
    mfaStatus: () => [...queryKeys.auth.all, 'mfa-status'] as const,
  },

  // Users queries
  users: {
    all: ['users'] as const,
    list: (filters?: UserFilters) => [...queryKeys.users.all, 'list', filters] as const,
    create: () => [...queryKeys.users.all, 'create'] as const,
    resend: (id: string) => [...queryKeys.users.all, 'resend', id] as const,
    delete: (id: string) => [...queryKeys.users.all, 'delete', id] as const,
  },

  // Tenant queries
  tenants: {
    all: ['tenants'] as const,
    lists: () => [...queryKeys.tenants.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tenants.details(), id] as const,
    userTenants: (resellerId?: string, filters?: any) =>
      [...queryKeys.tenants.all, 'user', resellerId, filters] as const,
    byReseller: (resellerId: string) =>
      [...queryKeys.tenants.lists(), 'reseller', resellerId] as const,
    myDetails: () => [...queryKeys.tenants.details(), 'me'] as const,
  },

  // Public queries
  public: {
    all: ['public'] as const,
    createTenant: () => [...queryKeys.public.all, 'create-tenant'] as const,
  },

  // Health queries
  health: {
    all: ['health'] as const,
    check: () => [...queryKeys.health.all, 'check'] as const,
  },

  // File management queries
  files: {
    all: ['files'] as const,
    lists: () => [...queryKeys.files.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.files.lists(), filters] as const,
    details: () => [...queryKeys.files.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.files.details(), id] as const,
    folderDetails: (id: string) => [...queryKeys.files.all, 'folder-details', id] as const,
    lookup: (name: string, parentId: string) =>
      [...queryKeys.files.all, 'lookup', name, parentId] as const,
    downloads: () => [...queryKeys.files.all, 'download'] as const,
    download: (id: string) => [...queryKeys.files.downloads(), id] as const,
  },

  // Audit trail queries
  auditTrail: {
    all: ['audit-trail'] as const,
    lists: () => [...queryKeys.auditTrail.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.auditTrail.lists(), filters] as const,
    details: () => [...queryKeys.auditTrail.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.auditTrail.details(), id] as const,
  },

  // Security queries
  security: {
    all: ['security'] as const,
    entropy: () => [...queryKeys.security.all, 'entropy'] as const,
    noise: () => [...queryKeys.security.all, 'noise'] as const,
    keypair: () => [...queryKeys.security.all, 'keypair'] as const,
  },

  // Reseller queries
  resellers: {
    all: ['resellers'] as const,
    lists: () => [...queryKeys.resellers.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.resellers.lists(), filters] as const,
    details: () => [...queryKeys.resellers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.resellers.details(), id] as const,
    stats: () => [...queryKeys.resellers.all, 'stats'] as const,
    usage: (id: string) => [...queryKeys.resellers.all, 'usage', id] as const,
  },

  // Plan queries
  plans: {
    all: ['plans'] as const,
    lists: () => [...queryKeys.plans.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.plans.lists(), filters] as const,
    details: () => [...queryKeys.plans.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.plans.details(), id] as const,
    tenants: (id: string, filters?: any) =>
      [...queryKeys.plans.detail(id), 'tenants', filters] as const,
    myQpaServices: () => [...queryKeys.plans.all, 'my-qpa-services'] as const,
  },

  // Invitation queries
  invitations: {
    all: ['invitations'] as const,
    lists: () => [...queryKeys.invitations.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.invitations.lists(), filters] as const,
    details: () => [...queryKeys.invitations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.invitations.details(), id] as const,
  },

  // Bulk invitation queries (QSE-994)
  bulkInvitations: {
    all: ['bulk-invitations'] as const,
    lists: () => [...queryKeys.bulkInvitations.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.bulkInvitations.lists(), filters] as const,
    details: () => [...queryKeys.bulkInvitations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.bulkInvitations.details(), id] as const,
    rowDetailsList: () => [...queryKeys.bulkInvitations.all, 'row-details'] as const,
    rowDetails: (id: string, filters?: any) =>
      [...queryKeys.bulkInvitations.rowDetailsList(), id, filters] as const,
  },

  // Quota queries
  quota: {
    all: ['quota'] as const,
    userSummary: () => [...queryKeys.quota.all, 'user-summary'] as const,
    tenantSummary: (tenantId?: string) =>
      [...queryKeys.quota.all, 'tenant-summary', tenantId] as const,
  },

  // API Key queries
  apiKeys: {
    all: ['api-keys'] as const,
    current: () => [...queryKeys.apiKeys.all, 'current'] as const,
  },

  // Profile Picture queries
  profilePicture: {
    all: ['profile-picture'] as const,
    current: () => [...queryKeys.profilePicture.all, 'current'] as const,
  },

  // Reports queries (formerly Storage Reports)
  reports: {
    all: ['reports'] as const,
    lists: () => [...queryKeys.reports.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.reports.lists(), filters] as const,
    tenantAnalytics: (tenantId: string) =>
      [...queryKeys.reports.all, 'tenant-analytics', tenantId] as const,
    tenantAnalyticsWithFilters: (tenantId: string, filters?: any) =>
      [...queryKeys.reports.tenantAnalytics(tenantId), filters] as const,
    latestSnapshot: (tenantId: string) =>
      [...queryKeys.reports.all, 'latest-snapshot', tenantId] as const,
  },

  // Billing queries
  billing: {
    all: ['billing'] as const,
    stripeProducts: () => [...queryKeys.billing.all, 'stripe-products'] as const,
    checkoutSessionStatus: (sessionId: string) =>
      [...queryKeys.billing.all, 'checkout-session', sessionId, 'status'] as const,
  },
} as const
